/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BulkFieldGroupUpdate } from '../models/BulkFieldGroupUpdate';
import type { ConvertToAssignablePayload } from '../models/ConvertToAssignablePayload';
import type { ConvertToPeriodicPayload } from '../models/ConvertToPeriodicPayload';
import type { ConvertToStaticPayload } from '../models/ConvertToStaticPayload';
import type { EntityFiltersPayload } from '../models/EntityFiltersPayload';
import type { EntityRead } from '../models/EntityRead';
import type { FieldAssignmentPayload } from '../models/FieldAssignmentPayload';
import type { FieldCreatePayload } from '../models/FieldCreatePayload';
import type { FieldDefinition } from '../models/FieldDefinition';
import type { FieldDimensionWithFieldValues } from '../models/FieldDimensionWithFieldValues';
import type { FieldFileImportRequest } from '../models/FieldFileImportRequest';
import type { FieldGroupCreatePayload } from '../models/FieldGroupCreatePayload';
import type { FieldGroupRead } from '../models/FieldGroupRead';
import type { FieldRead } from '../models/FieldRead';
import type { FieldScenarioCreate } from '../models/FieldScenarioCreate';
import type { FieldScenarioDependencies } from '../models/FieldScenarioDependencies';
import type { FieldScenarioRead } from '../models/FieldScenarioRead';
import type { FieldScenarioUpdate } from '../models/FieldScenarioUpdate';
import type { FieldsToEntityAssignmentPayload } from '../models/FieldsToEntityAssignmentPayload';
import type { FieldValuesFilters } from '../models/FieldValuesFilters';
import type { FormulaAssignmentPayload } from '../models/FormulaAssignmentPayload';
import type { FormulaRead } from '../models/FormulaRead';
import type { FormulasToEntityAssignmentPayload } from '../models/FormulasToEntityAssignmentPayload';
import type { FormulaTracebackRequest } from '../models/FormulaTracebackRequest';
import type { ImportResult } from '../models/ImportResult';
import type { ListAuditLogsForFieldValuesPayload } from '../models/ListAuditLogsForFieldValuesPayload';
import type { PaginatedResult } from '../models/PaginatedResult';
import type { PeriodicFieldWithFieldValues } from '../models/PeriodicFieldWithFieldValues';
import type { PeriodTypeEnum } from '../models/PeriodTypeEnum';
import type { ReferenceFieldEnum } from '../models/ReferenceFieldEnum';
import type { RestateFieldValuesPayload } from '../models/RestateFieldValuesPayload';
import type { StaticFieldWithFieldValues } from '../models/StaticFieldWithFieldValues';
import type { Traceback } from '../models/Traceback';
import type { TrackedFieldScenarioRead } from '../models/TrackedFieldScenarioRead';
import type { UpdateGroupIndicesPayload } from '../models/UpdateGroupIndicesPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class FieldsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Fields For Namespace Id
     * @param namespaceId
     * @param entityId
     * @param references
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listFieldsForNamespaceId(
        namespaceId: string,
        entityId?: (string | null),
        references?: (Array<ReferenceFieldEnum> | null),
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-fields-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'entity_id': entityId,
                'references': references,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field
     * @param fieldId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public getField(
        fieldId: string,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/get-field/{field_id}',
            path: {
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Group Indices For Namespace
     * @param namespaceId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateGroupIndicesForNamespace(
        namespaceId: string,
        requestBody: UpdateGroupIndicesPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/update-group-indices-for-namespace/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Fields With Values For Field
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns PeriodicFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public listFieldsWithValuesForField(
        clusterId: string,
        fieldId: string,
        requestBody: FieldValuesFilters,
    ): CancelablePromise<Array<PeriodicFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-fields-with-values-for-field',
            query: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Periodic Fields For Entity
     * @param entityId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listPeriodicFieldsForEntity(
        entityId: string,
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-periodic-fields-for-entity',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Static Fields For Entity
     * @param entityId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listStaticFieldsForEntity(
        entityId: string,
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-static-fields-for-entity',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Periodic Fields With Values For Entity
     * @param entityId
     * @param requestBody
     * @returns PeriodicFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public listPeriodicFieldsWithValuesForEntity(
        entityId: string,
        requestBody: FieldValuesFilters,
    ): CancelablePromise<Array<PeriodicFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-periodic-fields-with-values-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Static Fields With Values For Entity
     * @param entityId
     * @param requestBody
     * @returns StaticFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public listStaticFieldsWithValuesForEntity(
        entityId: string,
        requestBody: FieldValuesFilters,
    ): CancelablePromise<Array<StaticFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-static-fields-with-values-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Field Dimensions With Values For Entity
     * @param entityId
     * @returns FieldDimensionWithFieldValues Successful Response
     * @throws ApiError
     */
    public listFieldDimensionsWithValuesForEntity(
        entityId: string,
    ): CancelablePromise<Array<FieldDimensionWithFieldValues>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-field-dimensions-with-values-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Field Groups For Namespace Id
     * @param namespaceId
     * @returns FieldGroupRead Successful Response
     * @throws ApiError
     */
    public listFieldGroupsForNamespaceId(
        namespaceId: string,
    ): CancelablePromise<Array<FieldGroupRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-field-groups-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Field Group
     * @param fieldGroupId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFieldGroup(
        fieldGroupId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/fields/delete-field-group/{field_group_id}',
            path: {
                'field_group_id': fieldGroupId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Field Group For Namespace Id
     * This endpoints create a new field group and update other field groups index
     * to account for this new group
     * @param namespaceId
     * @param requestBody
     * @returns FieldGroupRead Successful Response
     * @throws ApiError
     */
    public createFieldGroupForNamespaceId(
        namespaceId: string,
        requestBody: FieldGroupCreatePayload,
    ): CancelablePromise<FieldGroupRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/create-field-group-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Field Groups For Namespace Id
     * @param namespaceId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateFieldGroupsForNamespaceId(
        namespaceId: string,
        requestBody: Array<BulkFieldGroupUpdate>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/update-field-groups-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Latest Field Date For Entity
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getLatestFieldDateForEntity(
        entityId: string,
    ): CancelablePromise<(string | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/get-latest-field-date-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Reference Fields For Namespace
     * Get all reference field definitions for a namespace (excluding NAME and LUMONIC_ID).
     * Returns the field schema without values - useful for building import UIs.
     * @param namespaceId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listReferenceFieldsForNamespace(
        namespaceId: string,
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-reference-fields-for-namespace/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Reference Fields With Values For Namespace
     * Get all reference field values for entities in a namespace
     * @param namespaceId
     * @returns StaticFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public listReferenceFieldsWithValuesForNamespace(
        namespaceId: string,
    ): CancelablePromise<Record<string, Record<string, StaticFieldWithFieldValues>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-reference-fields-with-values-for-namespace/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Reference Fields For Entity
     * @param entityId
     * @returns StaticFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public listReferenceFieldsForEntity(
        entityId: string,
    ): CancelablePromise<Record<string, StaticFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-reference-fields-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Static Field With Field Values By Entity Id For Field
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns StaticFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public getStaticFieldWithFieldValuesByEntityIdForField(
        clusterId: string,
        fieldId: string,
        requestBody: EntityFiltersPayload,
    ): CancelablePromise<Record<string, StaticFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/get-static-field-with-field-values-by-entity-id-for-field',
            query: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field Value By Entity Id For Temporal Field
     * @param fieldId
     * @param startDate
     * @param endDate
     * @param periodType
     * @returns PeriodicFieldWithFieldValues Successful Response
     * @throws ApiError
     */
    public getFieldValueByEntityIdForTemporalField(
        fieldId: string,
        startDate?: (string | null),
        endDate?: (string | null),
        periodType?: (PeriodTypeEnum | null),
    ): CancelablePromise<Record<string, PeriodicFieldWithFieldValues>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/get-field-value-by-entity-id-for-temporal-field/{field_id}',
            path: {
                'field_id': fieldId,
            },
            query: {
                'start_date': startDate,
                'end_date': endDate,
                'period_type': periodType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Fields For Entity
     * List all logs for all fields and field values for a given entity
     * @param namespaceId
     * @param entityId
     * @param isTemporal
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForFieldsForEntity(
        namespaceId: string,
        entityId: string,
        isTemporal: boolean = false,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-audit-logs-for-fields-for-entity/{namespace_id}/{entity_id}',
            path: {
                'namespace_id': namespaceId,
                'entity_id': entityId,
            },
            query: {
                'is_temporal': isTemporal,
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Field
     * When `entity_id` is provided: returns Field + FieldValue audits scoped to that entity.
     * When omitted: returns only field-definition audits (rename, formula, scope, etc.).
     *
     * The all-field-values case is intentionally excluded because it fans out to one
     * audit row per value write per entity per period per scenario, which is too
     * expensive on heavily-edited fields. Cell-level value history is available via
     * `list_audit_logs_for_field_values` using the cell's `field_value_id`(s).
     * @param fieldId
     * @param entityId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForField(
        fieldId: string,
        entityId?: (string | null),
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/list-audit-logs-for-field/{field_id}',
            path: {
                'field_id': fieldId,
            },
            query: {
                'entity_id': entityId,
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Field Values
     * List only field values log for a given field
     * @param fieldId
     * @param requestBody
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForFieldValues(
        fieldId: string,
        requestBody: ListAuditLogsForFieldValuesPayload,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-audit-logs-for-field-values/{field_id}',
            path: {
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Assigned Entities For Field
     * @param clusterId
     * @param fieldId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public listAssignedEntitiesForField(
        clusterId: string,
        fieldId: string,
    ): CancelablePromise<Array<EntityRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-assigned-entities-for-field',
            query: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Assigned Entity Ids By Field Id
     * @param clusterId
     * @param namespaceId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public getAssignedEntityIdsByFieldId(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Record<string, Array<EntityRead>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/get-assigned-entity-ids-by-field-id',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Assigned Entity Ids By Formula Id
     * @param clusterId
     * @param namespaceId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public getAssignedEntityIdsByFormulaId(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Record<string, Array<EntityRead>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/fields/get-assigned-entity-ids-by-formula-id',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Field To Entities
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignFieldToEntities(
        clusterId: string,
        fieldId: string,
        requestBody: FieldAssignmentPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/assign-field-to-entities',
            query: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Fields To Entity
     * @param clusterId
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignFieldsToEntity(
        clusterId: string,
        entityId: string,
        requestBody: FieldsToEntityAssignmentPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/assign-fields-to-entity',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Assigned Entities For Formula
     * @param clusterId
     * @param formulaId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public listAssignedEntitiesForFormula(
        clusterId: string,
        formulaId: string,
    ): CancelablePromise<Array<EntityRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/list-assigned-entities-for-formula',
            query: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Formula To Entities
     * @param clusterId
     * @param formulaId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignFormulaToEntities(
        clusterId: string,
        formulaId: string,
        requestBody: FormulaAssignmentPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/assign-formula-to-entities',
            query: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Formulas To Entity
     * @param clusterId
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignFormulasToEntity(
        clusterId: string,
        entityId: string,
        requestBody: FormulasToEntityAssignmentPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/assign-formulas-to-entity',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Traceback Formula
     * Get traceback information for a formula calculation.
     * Shows how the formula value was computed for a specific entity and date.
     * @param entityId
     * @param formulaId
     * @param requestBody
     * @returns Traceback Successful Response
     * @throws ApiError
     */
    public tracebackFormula(
        entityId: string,
        formulaId: string,
        requestBody: FormulaTracebackRequest,
    ): CancelablePromise<Traceback> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/fields/traceback-formula',
            query: {
                'entity_id': entityId,
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Field Value Restatements
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveFieldValueRestatements(
        entityId: string,
        requestBody: RestateFieldValuesPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/fields/save-field-value-restatements',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Formulas For Entity
     * @param entityId
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public listFormulasForEntity(
        entityId: string,
    ): CancelablePromise<Array<FormulaRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/fields/list-formulas-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Field Value For Id
     * Delete a field value by its ID.
     *
     * Args:
     * entity_id: The entity ID (for permission checking)
     * field_value_id: The ID of the field value to delete
     *
     * Returns:
     * None
     * @param entityId
     * @param fieldValueId
     * @param fieldId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFieldValueForId(
        entityId: string,
        fieldValueId: string,
        fieldId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/fields/delete-field-value/{field_value_id}',
            path: {
                'entity_id': entityId,
                'field_value_id': fieldValueId,
            },
            query: {
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Field For Cluster
     * @param clusterId
     * @param requestBody
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public createFieldForCluster(
        clusterId: string,
        requestBody: FieldCreatePayload,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/create-field-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Field Scenario For Cluster
     * @param clusterId
     * @param requestBody
     * @returns FieldScenarioRead Successful Response
     * @throws ApiError
     */
    public createFieldScenarioForCluster(
        clusterId: string,
        requestBody: FieldScenarioCreate,
    ): CancelablePromise<FieldScenarioRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/create-field-scenario-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create And Track Scenario For Namespace
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns FieldScenarioRead Successful Response
     * @throws ApiError
     */
    public createAndTrackScenarioForNamespace(
        clusterId: string,
        namespaceId: string,
        requestBody: FieldScenarioCreate,
    ): CancelablePromise<FieldScenarioRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/create-and-track-scenario-for-namespace',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Field Scenario For Cluster
     * @param clusterId
     * @param fieldScenarioId
     * @param requestBody
     * @returns FieldScenarioRead Successful Response
     * @throws ApiError
     */
    public updateFieldScenarioForCluster(
        clusterId: string,
        fieldScenarioId: string,
        requestBody: FieldScenarioUpdate,
    ): CancelablePromise<FieldScenarioRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/update-field-scenario-for-cluster/{field_scenario_id}',
            path: {
                'cluster_id': clusterId,
                'field_scenario_id': fieldScenarioId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tracked Field Scenarios For Cluster
     * @param clusterId
     * @returns TrackedFieldScenarioRead Successful Response
     * @throws ApiError
     */
    public listTrackedFieldScenariosForCluster(
        clusterId: string,
    ): CancelablePromise<Array<TrackedFieldScenarioRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/list-field-scenarios-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field Scenario Dependencies For Cluster
     * @param clusterId
     * @param fieldScenarioId
     * @returns FieldScenarioDependencies Successful Response
     * @throws ApiError
     */
    public getFieldScenarioDependenciesForCluster(
        clusterId: string,
        fieldScenarioId: string,
    ): CancelablePromise<FieldScenarioDependencies> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/fields/field-scenario-dependencies/{field_scenario_id}',
            path: {
                'cluster_id': clusterId,
                'field_scenario_id': fieldScenarioId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Field Scenario For Cluster
     * @param clusterId
     * @param fieldScenarioId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFieldScenarioForCluster(
        clusterId: string,
        fieldScenarioId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/fields/delete-field-scenario/{field_scenario_id}',
            path: {
                'cluster_id': clusterId,
                'field_scenario_id': fieldScenarioId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Set Default Field Scenario For Cluster
     * @param clusterId
     * @param fieldScenarioId
     * @returns FieldScenarioRead Successful Response
     * @throws ApiError
     */
    public setDefaultFieldScenarioForCluster(
        clusterId: string,
        fieldScenarioId: string,
    ): CancelablePromise<FieldScenarioRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/set-default-field-scenario/{field_scenario_id}',
            path: {
                'cluster_id': clusterId,
                'field_scenario_id': fieldScenarioId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Stop Tracking Field Scenario For Namespace
     * @param clusterId
     * @param namespaceId
     * @param fieldScenarioId
     * @returns any Successful Response
     * @throws ApiError
     */
    public stopTrackingFieldScenarioForNamespace(
        clusterId: string,
        namespaceId: string,
        fieldScenarioId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/fields/stop-tracking-field-scenario-for-namespace/{namespace_id}/{field_scenario_id}',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
                'field_scenario_id': fieldScenarioId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Field To Global
     * Convert an assignable field to a global field.
     * This will remove all entity assignments and make the field available to all entities.
     * @param clusterId
     * @param fieldId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public convertFieldToGlobal(
        clusterId: string,
        fieldId: string,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-field-to-global/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Field To Periodic
     * Convert a static field to periodic, stamping its values onto the chosen period and date.
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public convertFieldToPeriodic(
        clusterId: string,
        fieldId: string,
        requestBody: ConvertToPeriodicPayload,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-field-to-periodic/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Field To Static
     * Convert a periodic field to static, collapsing each entity to a single value.
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public convertFieldToStatic(
        clusterId: string,
        fieldId: string,
        requestBody: ConvertToStaticPayload,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-field-to-static/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Field To Assignable
     * Convert a global field to an assignable field and assign it to the specified entities.
     * @param clusterId
     * @param fieldId
     * @param requestBody
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public convertFieldToAssignable(
        clusterId: string,
        fieldId: string,
        requestBody: ConvertToAssignablePayload,
    ): CancelablePromise<FieldRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-field-to-assignable/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Formula To Global
     * Convert an assignable formula to a global formula.
     * This will remove all entity assignments and make the formula available to all entities.
     * @param clusterId
     * @param formulaId
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public convertFormulaToGlobal(
        clusterId: string,
        formulaId: string,
    ): CancelablePromise<FormulaRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-formula-to-global/{formula_id}',
            path: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Convert Formula To Assignable
     * Convert a global formula to an assignable formula and assign it to the specified entities.
     * @param clusterId
     * @param formulaId
     * @param requestBody
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public convertFormulaToAssignable(
        clusterId: string,
        formulaId: string,
        requestBody: ConvertToAssignablePayload,
    ): CancelablePromise<FormulaRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/convert-formula-to-assignable/{formula_id}',
            path: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field Import Fields
     * Get field definitions for field/formula imports.
     *
     * This endpoint provides the schema for the import modal:
     * - Core fields (name, description, returnType, expression)
     * - Field configuration (isTemporal, aggregationType)
     * - Scope configuration (scope, assignedEntities)
     * - Organization (fieldGroup with dynamic field groups)
     * - Select field options
     *
     * Frontend uses this to build the field mapping UI dynamically.
     *
     * Returns:
     * List of field definitions with keys, labels, types, and requirements
     * @param clusterId
     * @param namespaceId
     * @returns FieldDefinition Successful Response
     * @throws ApiError
     */
    public getFieldImportFields(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Array<FieldDefinition>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/fields/get-field-import-fields/{namespace_id}',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field Value Import Fields
     * Get field definitions for field VALUE imports.
     *
     * This endpoint provides the schema for importing field values:
     * - entity_name: Entity to import values for
     * - field_name: Field to set values on
     * - value: The value to import
     * - effective_date: Date the value is effective
     * - period_type: Period type tracked for the namespace
     * - scenario_name: Optional scenario (defaults to Actuals)
     * - as_of_date: Optional - controls restatement vs correction behavior
     *
     * Returns:
     * List of field definitions with keys, labels, types, and requirements
     * @param clusterId
     * @param namespaceId
     * @returns FieldDefinition Successful Response
     * @throws ApiError
     */
    public getFieldValueImportFields(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Array<FieldDefinition>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/fields/get-field-value-import-fields/{namespace_id}',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Import Fields From File
     * Import fields and formulas from file data (CSV, Excel) uploaded via the UI.
     *
     * This endpoint:
     * - Receives mapped data from the frontend ImportModal
     * - Validates all rows and collects errors
     * - Determines field vs formula based on expression presence
     * - Only persists to database if no errors OR skip_validation_errors=True
     *
     * Args:
     * cluster_id: Cluster ID (from auth guard)
     * namespace_id: Target namespace for fields/formulas
     * request: Import request containing data and options
     *
     * Returns:
     * ImportResult with status, counts, and any errors
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns ImportResult Successful Response
     * @throws ApiError
     */
    public importFieldsFromFile(
        clusterId: string,
        namespaceId: string,
        requestBody: FieldFileImportRequest,
    ): CancelablePromise<ImportResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/fields/namespaces/{namespace_id}/import-fields-from-file',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
